const HIGHLIGHT_REGEX = /\{\{([\s\S]*?)\}\}/g;
const BOLD_REGEX = /\*\*(.*?)\*\*/g;
const STATE_BELOW = 0;
const STATE_BETWEEN = 1;
const STATE_AT_OR_ABOVE = 2;

class DynamicSavingsBar extends HTMLElement {
  connectedCallback() {
    if (!this.parsePayload()) return;

    this.messageDesktop = this.querySelector('[data-message-desktop]');
    this.messageMobile = this.querySelector('[data-message-mobile]');
    if (!this.messageDesktop || !this.messageMobile) return;

    this.connected = true;
    this.subscribeToCart();
    this.fetchInitialCart();
    this.refreshTooltip();
  }

  disconnectedCallback() {
    this.connected = false;
    this.unsubscribe?.();
    this.tooltipTeardowns?.forEach((fn) => fn?.());
    this.tooltipTeardowns = null;
  }

  refreshTooltip() {
    this.tooltipTeardowns?.forEach((fn) => fn?.());
    this.tooltipTeardowns = [];
    this.querySelectorAll('.dynamic-savings-bar__info-wrap').forEach((wrap) => {
      const teardown = setupSavingsTooltipPortal(wrap);
      if (teardown) this.tooltipTeardowns.push(teardown);
    });
  }

  parsePayload() {
    try {
      this.payload = JSON.parse(this.dataset.payload);
    } catch {
      return false;
    }
    const { checkpoints, percentages } = this.payload || {};
    return (
      Array.isArray(checkpoints) && checkpoints.length > 0 &&
      Array.isArray(percentages) && percentages.length === checkpoints.length
    );
  }

  grossCents(cart) {
    if (!cart) return 0;
    if (typeof cart.original_total_price === 'number') return cart.original_total_price;
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    return (cart.total_price || 0) + (cart.total_discount || 0);
  }

  subscribeToCart() {
    const pubsub = window.FoxTheme?.pubsub;
    if (!pubsub?.PUB_SUB_EVENTS) return;
    this.unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, (cartUpdateData) => {
      const cart = cartUpdateData?.cart;
      if (!cart) return;
      this.render(this.grossCents(cart));
    });
  }

  fetchInitialCart() {
    const cartUrl = window.FoxTheme?.routes?.cart_url || '/cart';
    fetch(`${cartUrl}.js`)
      .then((response) => response.json())
      .then((cart) => {
        if (!this.connected) return;
        this.render(this.grossCents(cart));
      })
      .catch(() => { });
  }

  formatBareDollars(cents) {
    return String(Math.max(0, Math.ceil(cents / 100)));
  }

  formatMoneyWithoutCurrency(cents) {
    return (cents / 100).toFixed(2);
  }

  computeSavings(cartCents, tierIndex) {
    const { percentages, is_percentage_discount: isPercentage } = this.payload;
    if (isPercentage) {
      return this.formatMoneyWithoutCurrency(Math.round((cartCents * percentages[tierIndex]) / 100));
    }
    return String(percentages[tierIndex]);
  }

  resolveTierState(cartCents) {
    const { checkpoints, percentages, is_percentage_discount: isPercentage } = this.payload;
    const lastTierIndex = checkpoints.length - 1;

    const tierDiscountCents = (i) =>
      isPercentage ? Math.round((cartCents * percentages[i]) / 100) : percentages[i] * 100;

    let currentTierIndex = -1;
    for (let i = 0; i < checkpoints.length; i++) {
      if (cartCents >= checkpoints[i]) currentTierIndex = i;
    }

    if (currentTierIndex === -1) {
      return {
        state: STATE_BELOW,
        x: this.formatBareDollars(checkpoints[0] - cartCents),
        y: String(percentages[0]),
        z: '',
      };
    }
    if (currentTierIndex === lastTierIndex) {
      return {
        state: STATE_AT_OR_ABOVE,
        x: '',
        y: String(percentages[lastTierIndex]),
        z: this.computeSavings(cartCents, lastTierIndex),
      };
    }
    const nextTierIndex = currentTierIndex + 1;
    const effectiveCartCents = cartCents - tierDiscountCents(currentTierIndex);
    return {
      state: STATE_BETWEEN,
      x: this.formatBareDollars(checkpoints[nextTierIndex] - effectiveCartCents),
      y: String(percentages[nextTierIndex]),
      z: this.computeSavings(cartCents, currentTierIndex),
    };
  }

  buildMessage(sources, { state, x, y, z }, iconHtml) {
    const template = sources[state] || '';
    const filled = template.replaceAll('[[x]]', x).replaceAll('[[y]]', y).replaceAll('[[z]]', z);
    return this.applyBold(this.applyHighlights(filled)).replaceAll('[[i]]', iconHtml);
  }

  render(cartTotalCents) {
    const cartCents = Number(cartTotalCents) || 0;
    const tier = this.resolveTierState(cartCents);
    const iconHtml = this.payload.icon_html || '';
    const desktopTexts = this.payload.texts || [];
    const mobileTexts = this.payload.texts_mobile?.length ? this.payload.texts_mobile : desktopTexts;
    this.messageDesktop.innerHTML = this.buildMessage(desktopTexts, tier, iconHtml);
    this.messageMobile.innerHTML = this.buildMessage(mobileTexts, tier, iconHtml);
    this.refreshTooltip();
  }

  applyHighlights(message) {
    return message.replace(
      HIGHLIGHT_REGEX,
      (_, captured) => `<span class="dynamic-savings-bar__highlight">${captured.trim()}</span>`
    );
  }

  applyBold(message) {
    return message.replace(
      BOLD_REGEX,
      (_, captured) => `<span class="dynamic-savings-bar__bold">${captured}</span>`
    );
  }
}

if (!customElements.get('dynamic-savings-bar')) {
  customElements.define('dynamic-savings-bar', DynamicSavingsBar);
}

// Shared tooltip portal — exposed for any savings-bar variant (announcement
// bar, cart drawer goal). Moves the tooltip to <body> on hover/focus so it
// escapes ancestor `transform`/`overflow:hidden` containing blocks (e.g.
// Swiper-wrapper transforms in the announcement bar). Returns a teardown.
function setupSavingsTooltipPortal(wrap) {
  const button = wrap?.querySelector('.dynamic-savings-bar__info');
  const tip = wrap?.querySelector('.dynamic-savings-bar__tooltip');
  if (!wrap || !button || !tip) return null;

  const homeParent = tip.parentNode;
  const homeNext = tip.nextSibling;
  let active = false;

  const position = () => {
    const rect = button.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const margin = 12;
    tip.style.left = '0px';
    tip.style.top = '0px';
    const tipWidth = tip.getBoundingClientRect().width;
    const triggerCenterX = rect.left + rect.width / 2;
    let left = triggerCenterX - tipWidth / 2;
    if (left < margin) left = margin;
    if (left + tipWidth > viewportW - margin) left = viewportW - margin - tipWidth;
    tip.style.left = `${left}px`;
    tip.style.top = `${rect.bottom -4}px`;
    tip.style.setProperty('--tip-arrow-x', `${triggerCenterX - left}px`);
  };

  const restoreToHome = () => {
    if (!homeParent || !homeParent.isConnected) {
      tip.remove();
      return;
    }
    const ref = homeNext && homeParent.contains(homeNext) ? homeNext : null;
    homeParent.insertBefore(tip, ref);
  };

  const show = () => {
    if (active) return;
    active = true;
    if (tip.parentNode !== document.body) document.body.appendChild(tip);
    tip.dataset.tipState = 'shown';
    position();
  };

  const hide = () => {
    if (!active) return;
    active = false;
    tip.dataset.tipState = 'hidden';
    restoreToHome();
  };

  const onScrollOrResize = () => active && position();

  wrap.addEventListener('mouseenter', show);
  wrap.addEventListener('mouseleave', hide);
  button.addEventListener('focus', show);
  button.addEventListener('blur', hide);
  // Tooltip is `position: fixed` and lives in <body> while shown, so its
  // coordinates must follow viewport changes; window-scoped is correct here.
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  return () => {
    wrap.removeEventListener('mouseenter', show);
    wrap.removeEventListener('mouseleave', hide);
    button.removeEventListener('focus', show);
    button.removeEventListener('blur', hide);
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    if (active) hide();
    if (tip.parentNode === document.body) tip.remove();
  };
}

window.setupSavingsTooltipPortal = setupSavingsTooltipPortal;
